// src/App.js
import React, { useContext, useEffect, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";

import { AuthContext } from "./contexts/AuthContext";
import IFoodLayoutWrapper from "./components/layout/IFoodLayoutWrapper";
import SessionExpiredDialog from "./components/auth/SessionExpiredDialog";
import { debugLog } from "./utils/debug";
import ErrorBoundary from "./components/ErrorBoundary";

// Páginas públicas
const LandingPage = React.lazy(() => import("./pages/LandingPage"));
const LoginPage = React.lazy(() => import("./pages/LoginPage"));
const RegisterPage = React.lazy(() => import("./pages/RegisterPage.jsx"));
const SejaFranqueado = React.lazy(() => import("./pages/SejaFranqueado"));

// Páginas protegidas
const MainMenu = React.lazy(() => import("./pages/MainMenu"));
const StrategicHealthPage = React.lazy(() => import("./pages/StrategicHealthPage"));
const AdvisorPage = React.lazy(() => import("./pages/AdvisorPage"));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const MarketingPage = React.lazy(() => import('./pages/marketing'));
const DeliveryPage = React.lazy(() => import("./pages/delivery"));
const PratosPage = React.lazy(() => import("./pages/pratos"));
const FaturamentoPage = React.lazy(() => import("./pages/faturamento"));
const DesempenhoPage = React.lazy(() => import("./pages/DesempenhoPage"));
const DrePage = React.lazy(() => import("./pages/dre/DrePage"));
const SaidaPage = React.lazy(() => import("./pages/financeiro/SaidasPage"));
const EntradaPage = React.lazy(() => import("./pages/EntradaPage"));
const FaltasPage = React.lazy(() => import("./pages/FaltasPage"));
const NotFoundPage = React.lazy(() => import("./pages/NotFoundPage"));
const PainelOperacional = React.lazy(() => import("./pages/PainelOperacional"));
const ReviewsPage = React.lazy(() => import("./pages/avaliacoes/ReviewsPage"));
const LancamentoDREPage = React.lazy(() => import("./pages/financeiro/LancamentoDREPage"));
const ProductsPage = React.lazy(() => import("./pages/operacional/ProductsPage"));
const RelatorioOperacionalPage = React.lazy(() => import("./pages/operacao/RelatorioOperacionalPage"));
// BI do PDV: lê a réplica do caixa da loja, não o vochica_db.
const OperacaoPdvPage = React.lazy(() => import("./pages/bi-pdv/OperacaoPage"));
const AnalisePdvPage = React.lazy(() => import("./pages/bi-pdv/AnalisePage"));
const RecorrenciaBuffetPage = React.lazy(() => import("./pages/RecorrenciaBuffetPage"));
const EntrevistasPage = React.lazy(() => import("./pages/EntrevistasPage"));
const AttachmentsPage = React.lazy(() => import("./pages/anexos"));
const DocumentosPage = React.lazy(() => import("./pages/documentos"));
const FichasCustosPage = React.lazy(() => import("./pages/fichas-custos"));
const FormacaoPage = React.lazy(() => import("./pages/formacao"));
const AvaliacaoMensalPage = React.lazy(() => import("./pages/formacao/AvaliacaoMensalPage"));
// Manuais do colaborador e do coordenador, em abas, servidos de /public/docs.
const GestaoPessoasPage = React.lazy(() => import("./pages/gestao-pessoas"));
const LogisticaDashboard = React.lazy(() => import("./pages/logistica/LogisticaDashboard"));
const PedidosPage = React.lazy(() => import("./pages/operacao/PedidosPage"));
const FluxoCaixaPage = React.lazy(() => import("./pages/financeiro/FluxoCaixaPage"));
const FluxoCaixaForm = React.lazy(() => import("./pages/financeiro/FluxoCaixaForm"));
const ChecklistPage = React.lazy(() => import("./pages/operacional/ChecklistPage"));
const ChecklistCoordenacaoPage = React.lazy(() => import("./pages/operacional/ChecklistCoordenacaoPage"));

// Engenharia de Cardápio Pages
const CardapioLayout = React.lazy(() => import("./pages/cardapio/CardapioLayout"));
const PainelCardapioPage = React.lazy(() => import("./pages/cardapio/PainelCardapioPage"));
const InsumosPage = React.lazy(() => import("./pages/cardapio/InsumosPage"));
const ReceitasPage = React.lazy(() => import("./pages/cardapio/ReceitasPage"));
const PratosFichaPage = React.lazy(() => import("./pages/cardapio/PratosFichaPage"));
const CanaisPage = React.lazy(() => import("./pages/cardapio/CanaisPage"));
const PrecificacaoPage = React.lazy(() => import("./pages/cardapio/PrecificacaoPage"));
const CategoriasPage = React.lazy(() => import("./pages/cardapio/CategoriasPage")); // To be created

// Cold Room & Production

// Admin Pages
const AdminUserList = React.lazy(() => import("./pages/admin/AdminUserList"));
const AdminRoleManager = React.lazy(() => import("./pages/admin/AdminRoleManager"));
const AdminAuditLogs = React.lazy(() => import("./pages/admin/AdminAuditLogs"));
const AdminDatabaseExplorer = React.lazy(() => import("./pages/admin/AdminDatabaseExplorer"));
const MonitorDashboard = React.lazy(() => import("./pages/MonitorDashboard"));

// Dynamic Pages
const DynamicPageRenderer = React.lazy(() => import("./components/bi/DynamicPageRenderer"));
const ChartDesigner = React.lazy(() => import("./pages/bi/ChartDesigner"));

const LoadingFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <CircularProgress />
  </Box>
);

/* ─────────── Proteção de Rotas ─────────────────────────── */
const ProtectedRoute = ({ children }) => {
  const { auth } = useContext(AuthContext);

  useEffect(() => {
    debugLog("🛡️ ProtectedRoute: token =", auth?.token ? "OK" : "ausente");
  }, [auth]);

  return auth?.token ? children : <Navigate to="/login" replace />;
};

/* ─────────── Aplicação Principal ─────────────────────────── */
function App() {
  const { auth, sessionExpired, setSessionExpired } = useContext(AuthContext);

  useEffect(() => {
    debugLog("🚀 App montado — rotas carregadas");
  }, []);

  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingFallback />}>
        <CssBaseline /> {/* Global Reset */}
        
        {/* Aviso de Sessão Expirada */}
        <SessionExpiredDialog 
          open={sessionExpired} 
          onClose={() => setSessionExpired(false)} 
        />

        <Routes>
          {/* ─────────── Páginas públicas ─────────── */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/seja-franqueado" element={<SejaFranqueado />} />
          {/* Home pública do site. Estava importada sem rota — "/" vai para /login. */}
          <Route path="/landing" element={<LandingPage />} />

          {/* ─────────── Páginas protegidas com Layout Único (Estável) ─────────── */}
          <Route
            element={
              <ProtectedRoute>
                <IFoodLayoutWrapper />
              </ProtectedRoute>
            }
          >
            <Route path="/home" element={<MainMenu />} />
            <Route path="/health" element={<StrategicHealthPage />} />
            <Route path="/advisor" element={<AdvisorPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            
            {/* Rotas de BI e Operacional */}
            <Route path="/delivery" element={<DeliveryPage />} />
            <Route path="/faturamento" element={<FaturamentoPage />} />
            <Route path="/desempenho" element={<DesempenhoPage />} />
            <Route path="/dre" element={<DrePage />} />
            <Route path="/marketing" element={<MarketingPage />} />
            <Route path="/pratos" element={<PratosPage />} />
            <Route path="/entrada" element={<EntradaPage />} />
            <Route path="/saida" element={<SaidaPage />} />
            <Route path="/faltas" element={<FaltasPage />} />
            <Route path="/metas" element={<PainelOperacional />} />
            <Route path="/marketing/avaliacoes" element={<ReviewsPage />} />
            
            {/* Rotas de Admin */}
            <Route path="/admin/users" element={<AdminUserList />} />
            <Route path="/admin/roles" element={<AdminRoleManager />} />
            <Route path="/admin/audit" element={<AdminAuditLogs />} />
            <Route path="/admin/monitor" element={<MonitorDashboard />} />
            <Route path="/admin/database" element={<AdminDatabaseExplorer />} />
            
            {/* Dynamic Pages */}
            <Route path="/bi/designer" element={<ChartDesigner />} />
            <Route path="/dynamic/:slug" element={<DynamicPageRenderer />} />
            
            {/* Routes Financeiro */}
            <Route path="/financeiro/dre/lancamento" element={<LancamentoDREPage />} />
            <Route path="/financeiro/fluxo-caixa" element={<FluxoCaixaPage />} />
            <Route path="/financeiro/fluxo-caixa/novo" element={<FluxoCaixaForm />} />
            
            {/* Routes Operacional */}
            <Route path="/operacional/produtos" element={<ProductsPage />} />
            <Route path="/operacional/relatorio" element={<RelatorioOperacionalPage />} />

            {/* BI do PDV (banco bi_vochica) */}
            <Route path="/pdv/operacao" element={<OperacaoPdvPage />} />
            <Route path="/pdv/analise" element={<AnalisePdvPage />} />
            <Route path="/recorrencia-buffet" element={<RecorrenciaBuffetPage />} />
            <Route path="/entrevistas" element={<EntrevistasPage />} />
            <Route path="/operacional/checklists" element={<ChecklistPage />} />
            <Route path="/operacional/checklist-coordenacao" element={<ChecklistCoordenacaoPage />} />
            <Route path="/pedidos" element={<PedidosPage />} />
            <Route path="/anexos" element={<AttachmentsPage />} />
            <Route path="/documentos" element={<DocumentosPage />} />
            <Route path="/fichas-custos" element={<FichasCustosPage />} />
            <Route path="/logistica" element={<LogisticaDashboard />} />

            {/* Routes Gente & Formação */}
            <Route path="/formacao" element={<FormacaoPage />} />
            <Route path="/formacao/avaliacao-mensal" element={<AvaliacaoMensalPage />} />
            <Route path="/gestao-pessoas" element={<GestaoPessoasPage />} />

            {/* Routes Engenharia de Cardápio */}
            <Route path="/cardapio" element={<CardapioLayout />}>
              <Route path="painel" element={<PainelCardapioPage />} />
              <Route path="insumos" element={<InsumosPage />} />
              <Route path="categorias" element={<CategoriasPage />} />
              <Route path="receitas" element={<ReceitasPage />} />
              <Route path="pratos-ficha" element={<PratosFichaPage />} />
              <Route path="canais" element={<CanaisPage />} />
              <Route path="precificacao" element={<PrecificacaoPage />} />
            </Route>
          </Route>

          {/* ─────────── Página 404 ─────────── */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
